import apiService, { ServiceType } from './api';

/** One installer in the published release. `url` is the BE's authenticated download route. */
export interface DesktopReleaseAsset {
    file: string;
    /** Bytes. */
    size: number;
    sha256: string;
    url: string;
}

export interface DesktopRelease {
    version: string;
    publishedAt: string;
    notes?: string;
    platforms: Record<string, DesktopReleaseAsset>;
}

export type DesktopOs = 'macos' | 'windows';

export const LATEST_PATH = '/desktop/releases/latest';
export const downloadPath = (platform: string) => `/desktop/download/${encodeURIComponent(platform)}`;

const core = () => apiService.getService(ServiceType.CORE);

/** The latest release, or null when none is published (404). */
export async function getLatest(): Promise<DesktopRelease | null> {
    try {
        const { data } = await core().get<DesktopRelease>(LATEST_PATH);
        return data;
    } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
}

/** `filename*=UTF-8''…` wins over `filename="…"`, as browsers do. */
export function fileNameFromDisposition(header?: string | null): string | null {
    if (!header) return null;
    const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (star) return decodeURIComponent(star[1]);
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain ? plain[1] : null;
}

/**
 * Fetches the installer with the bearer token (an <a href> cannot send it), then saves the blob.
 * `onProgress` gets 0..1 when the length is known.
 */
export async function download(
    platform: string,
    fallbackName?: string,
    onProgress?: (fraction: number) => void,
): Promise<string> {
    const res = await core().get<Blob>(downloadPath(platform), {
        responseType: 'blob',
        headers: { Accept: 'application/octet-stream' },
        timeout: 0,
        onDownloadProgress: (e: { loaded: number; total?: number }) => {
            if (onProgress && e.total) onProgress(e.loaded / e.total);
        },
    });
    const name = fileNameFromDisposition(res.headers?.['content-disposition']) ?? fallbackName ?? platform;
    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return name;
}

type NavigatorLike = { userAgent?: string; userAgentData?: { platform?: string } };

/** The desktop OS this browser runs on; null for phones, Linux and anything we do not build for. */
export function detectOs(
    nav: NavigatorLike | undefined = typeof navigator !== 'undefined' ? (navigator as any) : undefined,
): DesktopOs | null {
    const hint = `${nav?.userAgentData?.platform ?? ''} ${nav?.userAgent ?? ''}`.toLowerCase();
    // iPhone/iPad UAs also say "Mac OS X"; they cannot run the app.
    if (/iphone|ipad|android/.test(hint)) return null;
    if (/mac/.test(hint)) return 'macos';
    if (/win/.test(hint)) return 'windows';
    return null;
}

/**
 * The manifest key to offer as the primary download. Keys are not fixed (a local build may
 * publish only `macos-arm64`), so pick by prefix with a preference order.
 * `arch` is a hint when known; browsers do not reliably report Apple Silicon.
 */
export function pickPrimary(keys: string[], os: DesktopOs | null, arch?: 'arm' | 'x86'): string | null {
    if (!os) return null;
    const prefs =
        os === 'macos'
            ? ['macos-universal', ...(arch === 'x86' ? ['macos-x64', 'macos-arm64'] : ['macos-arm64', 'macos-x64'])]
            : ['windows-x64-setup', 'windows-x64-msi'];
    return prefs.find((k) => keys.includes(k)) ?? keys.find((k) => k.startsWith(`${os}-`)) ?? null;
}

const LABELS: Record<string, string> = {
    'macos-universal': 'macOS (Universal)',
    'macos-arm64': 'macOS (Apple Silicon)',
    'macos-x64': 'macOS (Intel)',
    'windows-x64-setup': 'Windows installer (.exe)',
    'windows-x64-msi': 'Windows (.msi)',
};

export const platformLabel = (key: string): string => LABELS[key] ?? key;

export const osName = (key: string): string =>
    key.startsWith('macos-') ? 'macOS' : key.startsWith('windows-') ? 'Windows' : platformLabel(key);
